import type { ComponentProps } from "react"
import { Badge } from "@holigral/shared/ui/components/ui/badge"
import { AssignmentStatus } from "./model"
import { useT } from "@/i18n"

type BadgeStyle = Pick<ComponentProps<typeof Badge>, "variant" | "marker">

/**
 * The evaluator's view: the sun marks what is waiting on them.
 *
 * A submitted assignment is the only thing in this list that needs the person
 * reading it to do something, so it is the only one that takes the sun. In
 * progress gets the hollow ring — moving, but not theirs — and everything else
 * is border and fill alone.
 */
const EVALUATOR: Record<AssignmentStatus, BadgeStyle> = {
  [AssignmentStatus.Pending]: { variant: "pending" },
  [AssignmentStatus.InProgress]: { variant: "progress", marker: "ring" },
  [AssignmentStatus.Submitted]: { variant: "needs", marker: "dot" },
  [AssignmentStatus.Evaluated]: { variant: "done", marker: "check" },
  [AssignmentStatus.Revoked]: { variant: "revoked" },
}

/**
 * The participant's view: the same five states, with the sun moved to their
 * turn. Theirs are the ones they have not finished, so submitted — which is
 * the evaluator's problem now — loses its marker rather than gaining one.
 */
const PARTICIPANT: Record<AssignmentStatus, BadgeStyle> = {
  [AssignmentStatus.Pending]: { variant: "pending", marker: "sun" },
  [AssignmentStatus.InProgress]: { variant: "progress", marker: "sun" },
  [AssignmentStatus.Submitted]: { variant: "progress" },
  [AssignmentStatus.Evaluated]: { variant: "done", marker: "check" },
  [AssignmentStatus.Revoked]: { variant: "revoked" },
}

type Props = {
  readonly status: AssignmentStatus
  /**
   * Whose list this badge is in. The states are the same either way; which of
   * them carries the sun is not — submitted is the evaluator's move and the
   * participant's rest.
   *
   * Required, and deliberately: a default is how a respondent's own page ends
   * up telling them a finished assignment still needs them.
   */
  readonly viewer: "evaluator" | "participant"
}

export default function StatusBadge({ status, viewer }: Props) {
  const { t } = useT("assignment")
  const { variant, marker } =
    viewer === "evaluator" ? EVALUATOR[status] : PARTICIPANT[status]

  return (
    <Badge variant={variant} marker={marker}>
      {t(`status.${status}`)}
    </Badge>
  )
}
